import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  countByComunicadoCodigoAndPerfil,
  findByComunicadoCodigo,
  getEngajamentoInfo,
} from "./api-comunicado-usuario";
import { ALUNO, RESPONSAVEL } from "../../Usuario/Perfil";
import TablePaginationActions from "../../../shared/components/Table/Actions";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { dateTimeFormat } from "../../../util/DateUtil";
import Loading from "../../UI/Loading";

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

const toNumber = (value) => (typeof value === "number" ? value : 0);

const percent = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);

const StatCard = ({ label, value, total }) => (
  <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={600}>
        {value}
      </Typography>
      {total !== undefined && (
        <Stack direction="row" alignItems="center" spacing={1} mt={1}>
          <LinearProgress
            variant="determinate"
            value={percent(value, total)}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary">
            {percent(value, total)}%
          </Typography>
        </Stack>
      )}
    </Paper>
  </Grid>
);

const StatusIcon = ({ value, label }) => (
  <Tooltip title={value ? label : `Não ${label.toLowerCase()}`}>
    {value ? (
      <CheckCircleIcon fontSize="small" color="success" />
    ) : (
      <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
    )}
  </Tooltip>
);

export default function EngajamentoTable({ comunicado }) {
  const [comunicadoUsuarios, setComunicadoUsuarios] = useState(EMPTY_PAGE);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [engajamento, setEngajamento] = useState(null);
  const [quantAlunos, setQuantAlunos] = useState(0);
  const [quantResponsaveis, setQuantResponsaveis] = useState(0);

  useEffect(() => {
    setPageNumber(0);
  }, [comunicado.codigo]);

  useEffect(() => {
    let active = true;
    Promise.all([
      getEngajamentoInfo(comunicado.codigo),
      countByComunicadoCodigoAndPerfil(comunicado.codigo, ALUNO),
      countByComunicadoCodigoAndPerfil(comunicado.codigo, RESPONSAVEL),
    ]).then(([info, alunos, responsaveis]) => {
      if (!active) return;
      if (info?.error) {
        errorMessage(info.message);
      } else if (info) {
        setEngajamento(info);
      }
      setQuantAlunos(toNumber(alunos));
      setQuantResponsaveis(toNumber(responsaveis));
    });
    return () => {
      active = false;
    };
  }, [comunicado.codigo]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    findByComunicadoCodigo(comunicado.codigo, pageNumber)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setComunicadoUsuarios(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [comunicado.codigo, pageNumber]);

  const renderTable = () => {
    if (isLoading) {
      return (
        <Box py={4}>
          <Loading description="Carregando Engajamento..." />
        </Box>
      );
    }
    if (comunicadoUsuarios.content.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
          Nenhum destinatário encontrado.
        </Typography>
      );
    }
    return (
      <>
        <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 650 }} aria-label="Engajamento do comunicado">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
                <TableCell>Nome</TableCell>
                <TableCell>Perfil</TableCell>
                <TableCell align="center">Visualizado</TableCell>
                <TableCell align="center">Leitura Confirmada</TableCell>
                <TableCell>Enviado em</TableCell>
                <TableCell>Última Atualização</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comunicadoUsuarios.content.map((cu) => (
                <TableRow key={cu.codigo} hover>
                  <TableCell>
                    <Typography variant="body2">{cu.usuario.nome}</Typography>
                    {cu.usuario.perfil === RESPONSAVEL && cu.aluno && (
                      <Typography variant="caption" color="text.secondary">
                        Aluno: {cu.aluno.nome}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{cu.usuario.perfil}</TableCell>
                  <TableCell align="center">
                    <StatusIcon value={cu.visualizado} label="Visualizado" />
                  </TableCell>
                  <TableCell align="center">
                    <StatusIcon value={cu.leituraConfirmada} label="Leitura confirmada" />
                  </TableCell>
                  <TableCell>{dateTimeFormat(cu.createdAt)}</TableCell>
                  <TableCell>{dateTimeFormat(cu.lastModified)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          rowsPerPage={comunicadoUsuarios.size || 0}
          component="div"
          count={comunicadoUsuarios.totalElements}
          page={comunicadoUsuarios.number}
          onPageChange={(event, newPage) => setPageNumber(newPage)}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? `${count} registros` : `mais de ${to}`}`
          }
          ActionsComponent={TablePaginationActions}
        />
      </>
    );
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={600}>
        Engajamento
      </Typography>

      {engajamento && (
        <Grid container spacing={2}>
          <StatCard label="Total Enviado" value={toNumber(engajamento.total)} />
          <StatCard
            label="Visualizado"
            value={toNumber(engajamento.totalVisualizado)}
            total={toNumber(engajamento.total)}
          />
          <StatCard
            label="Leitura Confirmada"
            value={toNumber(engajamento.totalLeituraConfirmada)}
            total={toNumber(engajamento.total)}
          />
          <StatCard label="Alunos" value={quantAlunos} />
          <StatCard label="Responsáveis" value={quantResponsaveis} />
        </Grid>
      )}

      {renderTable()}
    </Stack>
  );
}
